import React, { useState, useEffect, useRef, useCallback } from 'react'

interface EditableCellProps {
  value: string | number | undefined | null
  onChange: (value: string | number | null) => void | Promise<void>
  onBlur?: () => void
  isLoading?: boolean
  isError?: boolean
  type?: 'text' | 'number' | 'currency'
  disabled?: boolean
  placeholder?: string
}

export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  onChange,
  onBlur,
  isLoading = false,
  isError = false,
  type = 'text',
  disabled = false,
  placeholder = ''
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [localValue, setLocalValue] = useState(String(value || ''))
  const inputRef = useRef<HTMLInputElement>(null)

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(String(value || ''))
  }, [value])

  // Auto-focus and select on edit start
  useEffect(() => {
    if (isEditing && inputRef.current && !disabled) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing, disabled])

  const handleSave = useCallback(async () => {
    if (localValue !== String(value || '')) {
      // Determine value based on type
      let finalValue: string | number | null = localValue

      if (type === 'number' || type === 'currency') {
        const parsedValue = parseFloat(localValue)
        finalValue = isNaN(parsedValue) ? null : parsedValue
      } else {
        // For text, empty string should be preserved or null if preferred
        finalValue = localValue === '' ? null : localValue
      }

      try {
        await onChange(finalValue)
      } catch (error) {
        console.error('Error saving cell:', error)
      }
    }
    setIsEditing(false)
    onBlur?.()
  }, [localValue, value, type, onChange, onBlur])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setLocalValue(String(value || ''))
        setIsEditing(false)
      }
    },
    [handleSave, value]
  )

  const formatDisplay = (val: any) => {
    if (val === null || val === undefined || val === '') return '—'
    if (type === 'currency') {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
      }).format(Number(val))
    }
    if (type === 'number') {
      return Number(val).toLocaleString('id-ID')
    }
    return String(val)
  }

  if (isEditing && !disabled) {
    return (
      <input
        ref={inputRef}
        type={type === 'currency' ? 'number' : type}
        step={type === 'number' || type === 'currency' ? 'any' : undefined}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={isLoading || disabled}
        className={`block w-full rounded-md border-0 py-1 px-2 text-slate-900 shadow-sm ring-1 ring-inset ring-primary-500 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 ${
          isError ? 'ring-red-500 focus:ring-red-500' : ''
        }`}
        placeholder={placeholder}
      />
    )
  }

  return (
    <div
      onClick={() => !disabled && setIsEditing(true)}
      className={`w-full px-2 py-1.5 rounded cursor-pointer text-sm transition-all duration-200 border border-transparent hover:border-slate-200 ${
        disabled
          ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
          : 'hover:bg-slate-50 cursor-text'
      } ${isError ? 'bg-red-50 text-red-700' : 'text-slate-700'} ${
        type === 'currency' || type === 'number' ? 'text-right tabular-nums' : ''
      }`}
      title={isLoading ? 'Saving...' : disabled ? 'Disabled' : 'Click to edit'}
    >
      {isLoading ? (
        <span className="text-slate-400 animate-pulse">Saving...</span>
      ) : (
        formatDisplay(value)
      )}
    </div>
  )
}

